// API client for the KontrataPH V3 server.
import { readToken, writeToken } from "./platform";

const API_BASE = ((import.meta as any).env?.VITE_API_ORIGIN ?? "").replace(/\/+$/, "") + "/api";

// Increment 45a: token storage is platform-aware — secure native store on mobile,
// localStorage on web (see lib/platform).
export function getToken(): string | null {
  return readToken();
}
function setToken(t: string | null) {
  writeToken(t);
}

async function req<T>(path: string, opts: RequestInit = {}): Promise<T> {
  const token = getToken();
  const res = await fetch(`${API_BASE}${path}`, {
    ...opts,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(opts.headers || {}),
    },
  });
  if (res.status === 401) return null as T;
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    // Increment 43: friendly messages for rate limiting + blocked content.
    if (res.status === 429) {
      const retry = (data as any)?.retryAfter;
      throw new Error((data as any)?.error || `You're doing that too fast. Please wait${retry ? ` ${retry}s` : " a moment"} and try again.`);
    }
    throw new Error((data as any)?.error || `HTTP ${res.status}`);
  }
  return data as T;
}

export interface AuthUser {
  id: number; email: string; firstName: string | null; lastName: string | null;
  profileImageUrl: string | null; userType: string | null;
  capabilities: string[]; activeContext: string | null;
  emailVerified: boolean; workerVerified: boolean; accountStatus: string;
}

export const api = {
  async signup(input: { firstName: string; lastName: string; email: string; password: string; activities: string[] }) {
    const r = await req<{ user: AuthUser; token: string }>("/auth/signup", { method: "POST", body: JSON.stringify(input) });
    if (r?.token) setToken(r.token);
    return r?.user ?? null;
  },
  async login(input: { email: string; password: string; rememberMe?: boolean }) {
    const r = await req<{ user: AuthUser; token: string }>("/auth/login", { method: "POST", body: JSON.stringify(input) });
    if (r?.token) setToken(r.token);
    return r?.user ?? null;
  },
  async logout() { await req("/auth/logout", { method: "POST" }); setToken(null); },
  async me() { const r = await req<{ user: AuthUser }>("/auth/me"); return r?.user ?? null; },
  async addActivity(activity: string) {
    const r = await req<{ user: AuthUser }>("/auth/activities", { method: "POST", body: JSON.stringify({ activity }) });
    return r?.user ?? null;
  },
  async switchContext(context: string | null) {
    const r = await req<{ user: AuthUser }>("/auth/context", { method: "POST", body: JSON.stringify({ context }) });
    return r?.user ?? null;
  },
  async getContexts() {
    return req<{ active: string | null; personal: { key: string; label: string }[]; companies: { key: string; label: string; initials: string; role: string }[] }>("/auth/contexts");
  },
  // Increment 23: email verification
  async sendVerifyEmail() {
    return req<{ ok: boolean; alreadyVerified?: boolean; throttled?: boolean; sent?: boolean; verifyUrl?: string | null }>("/auth/verify/send", { method: "POST" });
  },
  async confirmVerifyEmail(token: string) {
    return req<{ ok: boolean; user?: AuthUser }>("/auth/verify/confirm", { method: "POST", body: JSON.stringify({ token }) });
  },
  // Batch item 1: password reset (logged-out self-recovery)
  async forgotPassword(email: string) {
    return req<{ ok: boolean; resetUrl?: string | null }>("/auth/forgot-password", { method: "POST", body: JSON.stringify({ email }) });
  },
  async resetPassword(token: string, password: string) {
    return req<{ ok: boolean; error?: string }>("/auth/reset-password", { method: "POST", body: JSON.stringify({ token, password }) });
  },
  // Increment 24: change email
  async requestEmailChange(newEmail: string, password: string) {
    return req<{ ok: boolean; pendingEmail?: string; confirmUrl?: string | null }>("/auth/email/change", { method: "POST", body: JSON.stringify({ newEmail, password }) });
  },
  async confirmEmailChange(token: string) {
    return req<{ ok: boolean; user?: AuthUser; email?: string }>("/auth/email/confirm", { method: "POST", body: JSON.stringify({ token }) });
  },
  // Increment 35: email notification preference
  async getEmailPrefs() {
    return req<{ emailNotifications: boolean }>("/auth/email-prefs");
  },
  async setEmailPrefs(emailNotifications: boolean) {
    return req<{ ok: boolean; emailNotifications: boolean }>("/auth/email-prefs", { method: "PUT", body: JSON.stringify({ emailNotifications }) });
  },
  // Increment 37: categories
  async categories(group?: string) {
    const r = await req<{ categories: any[] }>(`/categories${group ? `?group=${encodeURIComponent(group)}` : ""}`);
    return r?.categories ?? [];
  },
  // Increment 47: identity edit + admin override
  async updateMyName(firstName: string, lastName: string) {
    return req<{ ok: boolean; reKycTriggered: boolean; user: AuthUser }>("/auth/profile", { method: "PATCH", body: JSON.stringify({ firstName, lastName }) });
  },
  async adminUpdateUser(id: number, patch: { firstName?: string; lastName?: string; email?: string }) {
    return req<{ ok: boolean; user: any }>(`/admin/users/${id}`, { method: "PATCH", body: JSON.stringify(patch) });
  },
  async adminResetPassword(id: number, password: string) {
    return req<{ ok: boolean }>(`/admin/users/${id}/password`, { method: "POST", body: JSON.stringify({ password }) });
  },
  async adminClearIdentityReview(id: number) {
    return req<{ ok: boolean }>(`/admin/users/${id}/clear-identity-review`, { method: "POST" });
  },
  // Batch item 9: analytics dashboard
  async adminAnalytics() {
    return req<{
      bookingsByMonth: { month: string; count: number }[];
      revenueByMonth: { month: string; revenue: number; gmv: number }[];
      scoreDistribution: Record<string, number>;
      topCategories: { category: string; count: number }[];
      totals: { revenue: number; gmv: number };
    }>("/admin/analytics");
  },
  // Batch item 10: user warnings
  async adminWarnUser(id: number, reason: string, note?: string) {
    return req<{ warning: any }>(`/admin/users/${id}/warn`, { method: "POST", body: JSON.stringify({ reason, note }) });
  },
  async adminUserWarnings(id: number) {
    const r = await req<{ warnings: any[] }>(`/admin/users/${id}/warnings`);
    return r?.warnings ?? [];
  },
  async adminCategories(group?: string) {
    const r = await req<{ categories: any[] }>(`/admin/categories${group ? `?group=${encodeURIComponent(group)}` : ""}`);
    return r?.categories ?? [];
  },
  async adminAddCategory(name: string, group?: string) {
    return req<{ category: any }>("/admin/categories", { method: "POST", body: JSON.stringify({ name, group }) });
  },
  async adminUpdateCategory(id: string, patch: { name?: string; active?: boolean; orderIndex?: number }) {
    return req<{ category: any }>(`/admin/categories/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(patch) });
  },
  async adminDeleteCategory(id: string) {
    return req<{ ok: boolean }>(`/admin/categories/${encodeURIComponent(id)}`, { method: "DELETE" });
  },
  // Increment 38: risk / fraud flags
  async reportFlag(input: { targetType: string; targetId: string; targetUserId?: number; reason: string; detail?: string }) {
    return req<{ ok: boolean; flagged: boolean; id: string | null; deduped: boolean }>("/flags", { method: "POST", body: JSON.stringify(input) });
  },
  async adminFlags(status = "open") {
    const r = await req<{ status: string; flags: any[] }>(`/admin/flags?status=${encodeURIComponent(status)}`);
    return r?.flags ?? [];
  },
  async adminFlagStats() {
    return req<Record<string, number>>("/admin/flags/stats");
  },
  async adminResolveFlag(id: string, resolution: "dismissed" | "content_hidden" | "user_suspended" | "no_action", note?: string) {
    return req<{ ok: boolean; id: string; status: string; resolution: string; applied: string }>(`/admin/flags/${encodeURIComponent(id)}/resolve`, { method: "POST", body: JSON.stringify({ resolution, note }) });
  },
  // Increment 39: legal documents (ToS / Privacy) + acceptance
  async legalDoc(kind: "terms" | "privacy") {
    const r = await req<{ document: { kind: string; version: number; title: string; body: string; publishedAt: string } }>(`/legal/${kind}`);
    return r?.document ?? null;
  },
  async legalStatus() {
    return req<{ terms: any; privacy: any; needsAcceptance: boolean }>("/legal/me/status");
  },
  async acceptLegal(kinds?: Array<"terms" | "privacy">) {
    return req<{ ok: boolean; accepted: any[]; status: any }>("/legal/accept", { method: "POST", body: JSON.stringify({ kinds }) });
  },
  async adminLegal() {
    return req<{ current: Record<string, number>; documents: any[] }>("/admin/legal");
  },
  async adminLegalDoc(id: string) {
    const r = await req<{ document: any }>(`/admin/legal/${encodeURIComponent(id)}`);
    return r?.document ?? null;
  },
  async adminPublishLegal(input: { kind: "terms" | "privacy"; title: string; body: string; publish?: boolean }) {
    return req<{ ok: boolean; document: any }>("/admin/legal", { method: "POST", body: JSON.stringify(input) });
  },
  // Increment 40: data rights (export + account deletion)
  async dataStatus() {
    return req<{ deletion: { scheduled: boolean; scheduledAt: string | null; requestedAt: string | null; graceDays: number } }>("/data/status");
  },
  async createDataExport() {
    return req<{ ok: boolean; export: { id: string; token: string; status: string; sizeBytes: number }; downloadPath: string }>("/data/export", { method: "POST" });
  },
  dataExportUrl(path: string) {
    // path already begins with /api/... ; strip the /api prefix since API_BASE adds it
    return (((import.meta as any).env?.VITE_API_ORIGIN ?? "").replace(/\/+$/, "")) + path;
  },
  async scheduleAccountDeletion() {
    return req<{ ok: boolean; scheduledAt: string; graceDays: number }>("/data/delete-account", { method: "POST" });
  },
  async cancelAccountDeletion() {
    return req<{ ok: boolean }>("/data/delete-account/cancel", { method: "POST" });
  },
  // Increment 41: support tickets
  async createTicket(input: { subject: string; category: string; body: string }) {
    return req<{ ok: boolean; ticket: any }>("/support", { method: "POST", body: JSON.stringify(input) });
  },
  async myTickets() {
    const r = await req<{ tickets: any[] }>("/support");
    return r?.tickets ?? [];
  },
  async ticket(id: string) {
    return req<{ ticket: any; messages: any[] }>(`/support/${encodeURIComponent(id)}`);
  },
  async replyTicket(id: string, body: string) {
    return req<{ ok: boolean; messages: any[] }>(`/support/${encodeURIComponent(id)}/reply`, { method: "POST", body: JSON.stringify({ body }) });
  },
  async adminTickets(status = "open") {
    const r = await req<{ status: string; tickets: any[] }>(`/admin/support?status=${encodeURIComponent(status)}`);
    return r?.tickets ?? [];
  },
  async adminTicket(id: string) {
    return req<{ ticket: any; messages: any[] }>(`/admin/support/${encodeURIComponent(id)}`);
  },
  async adminReplyTicket(id: string, body: string) {
    return req<{ ok: boolean; messages: any[] }>(`/admin/support/${encodeURIComponent(id)}/reply`, { method: "POST", body: JSON.stringify({ body }) });
  },
  async adminSetTicketStatus(id: string, status: "open" | "in_progress" | "resolved", outcome?: string, priority?: "low" | "normal" | "high") {
    return req<{ ok: boolean; status: string }>(`/admin/support/${encodeURIComponent(id)}/status`, { method: "POST", body: JSON.stringify({ status, outcome, priority }) });
  },
  // Increment 42: background jobs
  async adminJobs() {
    const r = await req<{ jobs: any[] }>("/admin/jobs");
    return r?.jobs ?? [];
  },
  async adminJobRuns(key?: string) {
    const r = await req<{ runs: any[] }>(`/admin/jobs/runs${key ? `?key=${encodeURIComponent(key)}` : ""}`);
    return r?.runs ?? [];
  },
  async adminRunJob(key: string) {
    return req<{ ok?: boolean; key?: string; summary?: any; error?: string; skipped?: boolean }>(`/admin/jobs/${encodeURIComponent(key)}/run`, { method: "POST" });
  },
  // Increment 36: web-push
  async pushVapidKey() {
    return req<{ publicKey: string; configured: boolean }>("/push/vapid-public-key");
  },
  async pushStatus() {
    return req<{ configured: boolean; count: number }>("/push/me");
  },
  async pushSubscribe(subscription: any) {
    return req<{ ok: boolean; count: number }>("/push/subscribe", { method: "POST", body: JSON.stringify({ subscription }) });
  },
  async pushUnsubscribe(endpoint: string) {
    return req<{ ok: boolean; count: number }>("/push/unsubscribe", { method: "POST", body: JSON.stringify({ endpoint }) });
  },
  async pushTest() {
    return req<{ ok: boolean; delivered: number }>("/push/test", { method: "POST" });
  },
  async list(
    kind: "people" | "services" | "companies" | "jobs" | "projects" | "rfqs",
    params?: Record<string, string | number | boolean | undefined>,
  ) {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params || {})) {
      if (v !== undefined && v !== "" && v !== false) qs.set(k, String(v));
    }
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    const r = await req<{ items: any[]; total: number }>(`/marketplace/${kind}${suffix}`);
    return r?.items ?? [];
  },
  // Increment 2: create a listing (throws on 4xx with the server message)
  async createListing(endpoint: string, payload: Record<string, unknown>) {
    return req<{ item: any; kind: string }>(`/listings/${endpoint}`, { method: "POST", body: JSON.stringify(payload) });
  },
  async listMine() {
    return req<{ jobs: any[]; services: any[]; projects: any[]; people: any[]; companies: any[]; rfqs: any[] }>("/listings/mine");
  },
  // Increment 3: Projects & Bids
  async getProject(id: string) {
    const r = await req<{ item: any }>(`/marketplace/projects/${encodeURIComponent(id)}`);
    return r?.item ?? null;
  },
  async getListing(kind: "people" | "services" | "companies" | "jobs" | "projects" | "rfqs", id: string) {
    const r = await req<{ item: any }>(`/marketplace/${kind}/${encodeURIComponent(id)}`);
    return r?.item ?? null;
  },
  async getBids(projectId: string) {
    return req<{
      isOwner: boolean; total: number; status: string; awardedBidId: string | null;
      bids: any[]; myBid?: any | null;
    }>(`/projects/${encodeURIComponent(projectId)}/bids`);
  },
  async submitBid(projectId: string, payload: Record<string, unknown>) {
    return req<{ bid: any; total: number }>(`/projects/${encodeURIComponent(projectId)}/bids`, {
      method: "POST", body: JSON.stringify(payload),
    });
  },
  async getNegotiation(projectId: string, bidId: string) {
    return req<{
      role: "owner" | "bidder"; projectStatus: string; bidStatus: string;
      openingPrice: number; standingPrice: number; finalPrice: number | null;
      bidderName: string; offers: { id: string; byRole: string; price: number; message: string | null; createdAt: string }[];
    }>(`/projects/${encodeURIComponent(projectId)}/bids/${encodeURIComponent(bidId)}/negotiation`);
  },
  async postCounter(projectId: string, bidId: string, payload: { price: number; message?: string }) {
    return req<{ ok: boolean; status: string; standingPrice: number }>(
      `/projects/${encodeURIComponent(projectId)}/bids/${encodeURIComponent(bidId)}/negotiate`,
      { method: "POST", body: JSON.stringify(payload) },
    );
  },
  async shortlistBid(projectId: string, bidId: string) {
    return req<{ bid: { id: string; status: string } }>(
      `/projects/${encodeURIComponent(projectId)}/bids/${encodeURIComponent(bidId)}/shortlist`,
      { method: "POST" },
    );
  },
  async awardBid(projectId: string, bidId: string) {
    return req<{ ok: boolean; awardedBidId: string; status: string; contract: any }>(
      `/projects/${encodeURIComponent(projectId)}/bids/${encodeURIComponent(bidId)}/award`,
      { method: "POST" },
    );
  },
  async myBids() {
    return req<{ bids: any[] }>("/projects/mine/bids");
  },

  // Increment 6: attachments (bytes proxied through the API → GCS/local) + Q&A
  async uploadAttachment(projectId: string, file: File) {
    const token = getToken();
    const url = `${API_BASE}/projects/${encodeURIComponent(projectId)}/attachments/upload?filename=${encodeURIComponent(file.name)}`;
    const res = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": file.type || "application/octet-stream",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: file,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error((data as any)?.error || `Upload failed (${res.status})`);
    return data as { attachment: any };
  },
  async listAttachments(projectId: string) {
    return req<{ attachments: any[]; total: number }>(`/projects/${encodeURIComponent(projectId)}/attachments`);
  },
  async deleteAttachment(projectId: string, attId: string) {
    return req<{ ok: boolean }>(`/projects/${encodeURIComponent(projectId)}/attachments/${encodeURIComponent(attId)}`, { method: "DELETE" });
  },
  attachmentDownloadUrl(projectId: string, attId: string) {
    return `${API_BASE}/projects/${encodeURIComponent(projectId)}/attachments/${encodeURIComponent(attId)}/download`;
  },
  async listQuestions(projectId: string) {
    return req<{ questions: any[]; total: number }>(`/projects/${encodeURIComponent(projectId)}/questions`);
  },
  async askQuestion(projectId: string, question: string) {
    return req<{ question: any }>(`/projects/${encodeURIComponent(projectId)}/questions`, { method: "POST", body: JSON.stringify({ question }) });
  },
  async answerQuestion(projectId: string, qid: string, answer: string) {
    return req<{ question: any }>(`/projects/${encodeURIComponent(projectId)}/questions/${encodeURIComponent(qid)}/answer`, { method: "POST", body: JSON.stringify({ answer }) });
  },

  // Increment 7: RFQ + itemized quotes
  async getRfq(id: string) {
    const r = await req<{ item: any }>(`/marketplace/rfqs/${encodeURIComponent(id)}`);
    return r?.item ?? null;
  },
  async getQuotes(rfqId: string) {
    return req<{
      isOwner: boolean; total: number; status: string; awardedQuoteId: string | null;
      items: any[]; quotes: any[]; myQuote?: any | null;
    }>(`/rfqs/${encodeURIComponent(rfqId)}/quotes`);
  },
  async submitQuote(rfqId: string, payload: Record<string, unknown>) {
    return req<{ quote: any; total: number }>(`/rfqs/${encodeURIComponent(rfqId)}/quotes`, { method: "POST", body: JSON.stringify(payload) });
  },
  async shortlistQuote(rfqId: string, qid: string) {
    return req<{ quote: { id: string; status: string } }>(`/rfqs/${encodeURIComponent(rfqId)}/quotes/${encodeURIComponent(qid)}/shortlist`, { method: "POST" });
  },
  async awardQuote(rfqId: string, qid: string) {
    return req<{ ok: boolean; awardedQuoteId: string; status: string; purchaseOrder: any }>(`/rfqs/${encodeURIComponent(rfqId)}/quotes/${encodeURIComponent(qid)}/award`, { method: "POST" });
  },
  async myQuotes() {
    return req<{ quotes: any[] }>("/rfqs/mine/quotes");
  },

  // Increment 8: messaging
  async startThread(input: { toUserId: number; contextType?: string; contextId?: string; subject?: string; body: string }) {
    return req<{ threadId: string; message: any }>("/threads", { method: "POST", body: JSON.stringify(input) });
  },
  async listThreads() {
    return req<{ threads: any[] }>("/threads");
  },
  async getThread(id: string) {
    return req<{ thread: any; messages: any[] }>(`/threads/${encodeURIComponent(id)}`);
  },
  async postMessage(threadId: string, body: string) {
    return req<{ message: any }>(`/threads/${encodeURIComponent(threadId)}/messages`, { method: "POST", body: JSON.stringify({ body }) });
  },

  // Increment 9: contracts + escrow
  async listContracts() {
    return req<{ contracts: any[] }>("/contracts");
  },
  async walletSummary() {
    return req<{ inEscrow: number; releasedByMe: number; earned: number; incoming: number; asBuyer: number; asProvider: number }>("/contracts/summary");
  },
  // Increment 34: platform fees (admin)
  async adminFees() {
    return req<{ feePct: number; totalCollected: number; recent: any[] }>("/admin/fees");
  },
  async adminSetFee(pct: number) {
    return req<{ ok: boolean; feePct: number }>("/admin/fees", { method: "POST", body: JSON.stringify({ pct }) });
  },
  // Increment 33: download a contract/receipt PDF (carries the Bearer token, then triggers a save)
  async downloadContractPdf(id: string, kind: "contract" | "receipt") {
    const token = getToken();
    const res = await fetch(`${API_BASE}/contracts/${encodeURIComponent(id)}/${kind}.pdf`, {
      credentials: "include", headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
    if (!res.ok) throw new Error("Could not generate the PDF.");
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${kind}-${id}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  },
  // Increment 32: contract checklist & check-ins
  async contractChecklist(id: string) {
    const r = await req<{ items: any[] }>(`/contracts/${encodeURIComponent(id)}/checklist`);
    return r?.items ?? [];
  },
  async addChecklistItem(id: string, label: string) {
    return req<{ item: any }>(`/contracts/${encodeURIComponent(id)}/checklist`, { method: "POST", body: JSON.stringify({ label }) });
  },
  async toggleChecklistItem(id: string, itemId: string) {
    return req<{ item: any }>(`/contracts/${encodeURIComponent(id)}/checklist/${encodeURIComponent(itemId)}/toggle`, { method: "POST" });
  },
  async deleteChecklistItem(id: string, itemId: string) {
    return req<{ ok: boolean }>(`/contracts/${encodeURIComponent(id)}/checklist/${encodeURIComponent(itemId)}`, { method: "DELETE" });
  },
  async contractCheckins(id: string) {
    const r = await req<{ checkins: any[] }>(`/contracts/${encodeURIComponent(id)}/checkins`);
    return r?.checkins ?? [];
  },
  async addCheckin(id: string, note: string) {
    return req<{ checkin: any }>(`/contracts/${encodeURIComponent(id)}/checkins`, { method: "POST", body: JSON.stringify({ note }) });
  },
  // Increment 31: contract amendments & cancellations
  async contractEvents(id: string) {
    const r = await req<{ events: any[] }>(`/contracts/${encodeURIComponent(id)}/events`);
    return r?.events ?? [];
  },
  async proposeAmendment(id: string, body: { newTitle?: string; newAmount?: number; message?: string }) {
    return req<{ event: any }>(`/contracts/${encodeURIComponent(id)}/amendments`, { method: "POST", body: JSON.stringify(body) });
  },
  async respondAmendment(id: string, eid: string, decision: "accept" | "reject") {
    return req<{ ok: boolean; status: string }>(`/contracts/${encodeURIComponent(id)}/amendments/${encodeURIComponent(eid)}/${decision}`, { method: "POST" });
  },
  async requestContractCancel(id: string, message?: string) {
    return req<{ ok: boolean; status: string; event: any }>(`/contracts/${encodeURIComponent(id)}/cancel`, { method: "POST", body: JSON.stringify({ message }) });
  },
  async respondContractCancel(id: string, eid: string, decision: "accept" | "reject") {
    return req<{ ok: boolean; status: string }>(`/contracts/${encodeURIComponent(id)}/cancel/${encodeURIComponent(eid)}/${decision}`, { method: "POST" });
  },
  async fundContract(id: string) {
    return req<{ contract: { id: string; status: string } }>(`/contracts/${encodeURIComponent(id)}/fund`, { method: "POST" });
  },
  async releaseMilestone(id: string, mid: string) {
    return req<{ ok: boolean; released: number; contractCompleted: boolean }>(`/contracts/${encodeURIComponent(id)}/milestones/${encodeURIComponent(mid)}/release`, { method: "POST" });
  },
  // Batch item 5: contract offer handshake
  async acceptOffer(id: string) {
    return req<{ ok: boolean; offerStatus: string }>(`/contracts/${encodeURIComponent(id)}/offer/accept`, { method: "POST" });
  },
  async declineOffer(id: string) {
    return req<{ ok: boolean; offerStatus: string; status: string }>(`/contracts/${encodeURIComponent(id)}/offer/decline`, { method: "POST" });
  },
  async withdrawOffer(id: string) {
    return req<{ ok: boolean; offerStatus: string; status: string }>(`/contracts/${encodeURIComponent(id)}/offer/withdraw`, { method: "POST" });
  },
  // Batch item 6: time tracking
  async contractTimeLogs(id: string) {
    return req<{ logs: any[]; totalMinutes: number; openLogId: string | null }>(`/contracts/${encodeURIComponent(id)}/time-logs`);
  },
  async contractTimeIn(id: string, note?: string) {
    return req<{ log: any }>(`/contracts/${encodeURIComponent(id)}/timein`, { method: "POST", body: JSON.stringify({ note }) });
  },
  async contractTimeOut(id: string, note?: string) {
    return req<{ log: any }>(`/contracts/${encodeURIComponent(id)}/timeout`, { method: "POST", body: JSON.stringify({ note }) });
  },
  // Batch item 7: progress log
  async contractProgress(id: string) {
    const r = await req<{ entries: any[] }>(`/contracts/${encodeURIComponent(id)}/progress`);
    return r?.entries ?? [];
  },
  async addContractProgress(id: string, note: string, logType?: "update" | "milestone" | "issue") {
    return req<{ entry: any }>(`/contracts/${encodeURIComponent(id)}/progress`, { method: "POST", body: JSON.stringify({ note, logType }) });
  },

  // Increment 10: wallet balance + GMPay cash-in/out
  async getWallet() {
    return req<{
      balance: number; available: number; held: number; pendingIn: number;
      canCashIn: boolean; canCashOut: boolean; provider: string; gmpayLive: boolean; gmpaySimulation: boolean;
      limits: { min: number; max: number }; transactions: any[];
    }>("/wallet");
  },
  async cashIn(amount: number, method: "qrph" | "card", mobile?: string) {
    return req<{ status: string; method: string; qrRaw?: string; checkoutUrl?: string; referenceNumber?: string; expiresInMinutes?: number; txnRef: string; balance: number; available: number }>(
      "/wallet/cashin", { method: "POST", body: JSON.stringify({ amount, method, mobile }) });
  },
  async cashOut(amount: number, receiver: { bankCode: string; bankName: string; accountNumber: string; receiverName: string }) {
    return req<{ status: string; txnRef: string; balance: number; available: number }>(
      "/wallet/cashout", { method: "POST", body: JSON.stringify({ amount, ...receiver }) });
  },
  async walletChannels() {
    return req<{ channels: { code: string; name: string }[] }>("/wallet/channels");
  },

  // Increment 11: Materials & Equipment store
  async listItems(params?: { q?: string; kind?: string; mode?: string; category?: string }) {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params || {})) {
      if (v !== undefined && v !== "") qs.set(k, String(v));
    }
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    const r = await req<{ items: any[] }>(`/items${suffix}`);
    return r?.items ?? [];
  },
  async getItem(id: string) {
    const r = await req<{ item: any }>(`/items/${encodeURIComponent(id)}`);
    return r?.item ?? null;
  },
  async createItem(payload: Record<string, unknown>) {
    return req<{ item: any }>("/items", { method: "POST", body: JSON.stringify(payload) });
  },
  async myItems() {
    const r = await req<{ items: any[] }>("/items/mine");
    return r?.items ?? [];
  },
  async updateItem(id: string, payload: Record<string, unknown>) {
    return req<{ item: any }>(`/items/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(payload) });
  },
  async archiveItem(id: string) {
    return req<{ item: any }>(`/items/${encodeURIComponent(id)}`, { method: "DELETE" });
  },
  async uploadItemImage(id: string, file: File) {
    const token = getToken();
    const res = await fetch(`${API_BASE}/items/${encodeURIComponent(id)}/image`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": file.type || "application/octet-stream",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: file,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error((data as any)?.error || `Upload failed (${res.status})`);
    return data as { item: any };
  },
  itemImageUrl(id: string) {
    return `${API_BASE}/items/${encodeURIComponent(id)}/image`;
  },
  async placeOrder(payload: { itemId: string; qty: number; rentDays?: number; paymentMethod?: "wallet" | "credit" }) {
    return req<{ order: any }>("/orders", { method: "POST", body: JSON.stringify(payload) });
  },
  async myOrders() {
    return req<{ purchases: any[]; sales: any[] }>("/orders/mine");
  },
  async fulfillOrder(id: string) {
    return req<{ order: any }>(`/orders/${encodeURIComponent(id)}/fulfill`, { method: "POST" });
  },
  async confirmOrder(id: string) {
    return req<{ order: any }>(`/orders/${encodeURIComponent(id)}/confirm`, { method: "POST" });
  },
  async cancelOrder(id: string) {
    return req<{ order: any }>(`/orders/${encodeURIComponent(id)}/cancel`, { method: "POST" });
  },

  // Increment 12: Ratings & Reviews
  async submitReview(payload: { dealType: "order" | "contract"; dealId: string; rating: number; comment?: string }) {
    return req<{ review: any }>("/reviews", { method: "POST", body: JSON.stringify(payload) });
  },
  async userReviews(userId: number) {
    return req<{ average: number; count: number; breakdown: Record<string, number>; reviews: any[] }>(`/reviews/user/${userId}`);
  },
  async myReviews() {
    return req<{ average: number; count: number; breakdown: Record<string, number>; received: any[]; written: any[] }>("/reviews/me");
  },
  async pendingReviews() {
    const r = await req<{ pending: any[] }>("/reviews/pending");
    return r?.pending ?? [];
  },

  // Increment 13: notifications
  async getNotifications() {
    return req<{ notifications: any[]; unread: number }>("/notifications");
  },
  async notifUnreadCount() {
    const r = await req<{ unread: number }>("/notifications/unread-count");
    return r?.unread ?? 0;
  },
  async markNotifRead(id: string) {
    return req<{ ok: boolean; unread: number }>(`/notifications/${encodeURIComponent(id)}/read`, { method: "POST" });
  },
  async markAllNotifRead() {
    return req<{ ok: boolean; unread: number }>("/notifications/read-all", { method: "POST" });
  },

  // Increment 14: profiles + verification
  async getProfile(userId: number) {
    return req<{ user: any; rating: any; reviews: any[]; listings: any; counts: any }>(`/profile/${userId}`);
  },
  async myVerification() {
    return req<{ verified: boolean; verifiedKind: string | null; request: any | null }>("/verification/me");
  },
  async submitVerification(kind: "id" | "company", label?: string) {
    return req<{ request: any }>("/verification", { method: "POST", body: JSON.stringify({ kind, label }) });
  },
  async pendingVerifications() {
    const r = await req<{ pending: any[] }>("/verification/pending");
    return r?.pending ?? [];
  },
  // Increment 25: Worker KYC (worker side)
  async workerKyc() {
    return req<{ workerVerified: boolean; verification: { status: string; idType: string | null; idNumber: string | null; decisionNote: string | null; reviewedAt: string | null; docs: { id_image: boolean; selfie: boolean; address_proof: boolean } } }>("/workers/verification/me");
  },
  async startWorkerKyc(idType: string, idNumber: string) {
    return req<{ verification: any }>("/workers/verification", { method: "POST", body: JSON.stringify({ idType, idNumber }) });
  },
  async uploadKycDoc(docType: "id_image" | "selfie" | "address_proof", file: File) {
    const token = getToken();
    const res = await fetch(`${API_BASE}/workers/verification/doc/${docType}`, {
      method: "POST", credentials: "include",
      headers: { "Content-Type": file.type || "application/octet-stream", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: file,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error((data as any)?.error || `HTTP ${res.status}`);
    return data;
  },
  async submitWorkerKyc() {
    return req<{ verification: any }>("/workers/verification/submit", { method: "POST" });
  },
  // Increment 25: Worker KYC (admin side)
  async adminWorkerVerifications(status = "pending") {
    const r = await req<{ verifications: any[] }>(`/admin/worker-verifications?status=${encodeURIComponent(status)}`);
    return r?.verifications ?? [];
  },
  async adminApproveWorkerVerification(id: string) {
    return req<{ ok: boolean }>(`/admin/worker-verifications/${encodeURIComponent(id)}/approve`, { method: "POST" });
  },
  async adminRejectWorkerVerification(id: string, note?: string) {
    return req<{ ok: boolean }>(`/admin/worker-verifications/${encodeURIComponent(id)}/reject`, { method: "POST", body: JSON.stringify({ note }) });
  },
  /** Fetch a KYC document as an object URL (carries the admin Bearer token). */
  async adminKycDocUrl(id: string, docType: "id_image" | "selfie" | "address_proof"): Promise<string | null> {
    const token = getToken();
    const res = await fetch(`${API_BASE}/admin/worker-verifications/${encodeURIComponent(id)}/doc/${docType}`, {
      credentials: "include", headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
    if (!res.ok) return null;
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  },
  // Increment 27: worker onboarding + profile
  async workerProfile() {
    return req<{ profile: any; completeness: number; missing: string[]; onboarded: boolean; personalLock: { locked: boolean; until: string | null; fields: string[] }; minDailyRate: number }>("/workers/profile/me");
  },
  async updateWorkerProfile(patch: Record<string, unknown>) {
    return req<{ profile: any; completeness: number; missing: string[]; onboarded: boolean; personalLock: any; minDailyRate: number }>("/workers/profile", { method: "PUT", body: JSON.stringify(patch) });
  },
  async completeWorkerOnboarding(payload: Record<string, unknown>) {
    return req<{ profile: any; completeness: number; onboarded: boolean; personalLock: any }>("/workers/onboarding/complete", { method: "POST", body: JSON.stringify(payload) });
  },
  // Increment 46a: worker payout account + earnings
  async myBankAccount() {
    return req<{ account: any | null }>("/workers/me/bank-account");
  },
  async saveBankAccount(input: { bankCode: string; accountName: string; accountNumber: string; ownershipConfirmed: boolean }) {
    return req<{ ok: boolean; payoutAccountChanged: boolean; message: string; account: any }>("/workers/me/bank-account", { method: "PUT", body: JSON.stringify(input) });
  },
  async myEarnings() {
    return req<{ availableBalance: number; pendingRelease: number; releasedTotal: number; weeklyEarnings: number; monthlyEarnings: number; earningsHistory: { label: string; value: number }[]; recentPayments: any[]; bankAccount: any | null }>("/workers/me/earnings");
  },
  // Increment 29: worker score & tier
  async myWorkerScore() {
    return req<{ finalScore: number; tier: "A" | "B" | "C"; breakdown: Record<string, number> }>("/workers/score/me");
  },
  // Batch item 2: worker portfolio / media
  async myMedia() {
    const r = await req<{ media: any[] }>("/workers/me/media");
    return r?.media ?? [];
  },
  async workerMedia(userId: number) {
    const r = await req<{ media: any[] }>(`/workers/${userId}/media`);
    return r?.media ?? [];
  },
  async uploadMedia(file: File, meta?: { title?: string; caption?: string }) {
    const token = getToken();
    const qs = new URLSearchParams();
    if (meta?.title) qs.set("title", meta.title);
    if (meta?.caption) qs.set("caption", meta.caption);
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    const res = await fetch(`${API_BASE}/workers/me/media${suffix}`, {
      method: "POST", credentials: "include",
      headers: { "Content-Type": file.type || "application/octet-stream", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: file,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error((data as any)?.error || `Upload failed (${res.status})`);
    return data as { media: any };
  },
  async updateMedia(id: string, meta: { title?: string; caption?: string }) {
    return req<{ media: any }>(`/workers/me/media/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(meta) });
  },
  async deleteMedia(id: string) {
    return req<{ ok: boolean }>(`/workers/me/media/${encodeURIComponent(id)}`, { method: "DELETE" });
  },
  async starStatus() {
    return req<{ isStar: boolean; starSince: string | null; eligible: boolean; requirements: { key: string; label: string; done: boolean }[] }>("/workers/star/status");
  },
  async claimStar() {
    return req<{ ok: boolean; isStar: boolean; alreadyStar?: boolean }>("/workers/star/claim", { method: "POST" });
  },
  async banners(slot?: string) {
    const r = await req<{ banners: any[] }>(`/banners${slot ? `?slot=${encodeURIComponent(slot)}` : ""}`);
    return r?.banners ?? [];
  },
  async adminMedia(status = "pending") {
    const r = await req<{ media: any[] }>(`/admin/media?status=${encodeURIComponent(status)}`);
    return r?.media ?? [];
  },
  async adminModerateMedia(id: string, decision: "approve" | "reject", note?: string) {
    return req<{ ok: boolean; status: string }>(`/admin/media/${encodeURIComponent(id)}/${decision}`, { method: "POST", body: JSON.stringify({ note }) });
  },
  async adminBanners() {
    const r = await req<{ banners: any[] }>("/admin/banners");
    return r?.banners ?? [];
  },
  async adminCreateBanner(b: Record<string, unknown>) {
    return req<{ banner: any }>("/admin/banners", { method: "POST", body: JSON.stringify(b) });
  },
  async adminUpdateBanner(id: string, b: Record<string, unknown>) {
    return req<{ banner: any }>(`/admin/banners/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(b) });
  },
  async adminDeleteBanner(id: string) {
    return req<{ ok: boolean }>(`/admin/banners/${encodeURIComponent(id)}`, { method: "DELETE" });
  },
  // Batch item 3: worker score history
  async myScoreHistory() {
    const r = await req<{ history: { finalScore: number; tier: string; computedAt: string }[] }>("/workers/me/score-history");
    return r?.history ?? [];
  },
  // Batch item 4: verification-queue position
  async myVerificationQueue() {
    return req<{ inQueue: boolean; status: string; position: number | null; total: number; submittedAt?: string }>("/workers/me/verification-queue");
  },
  // Increment 28: worker availability
  async myAvailability() {
    return req<{ status: string; slots: { id: string; dayOfWeek: number; startTime: string; endTime: string }[] }>("/workers/availability/me");
  },
  async setAvailabilityStatus(status: "available" | "busy" | "unavailable") {
    return req<{ status: string; slots: any[] }>("/workers/availability/status", { method: "PUT", body: JSON.stringify({ status }) });
  },
  async addAvailabilitySlot(dayOfWeek: number, startTime: string, endTime: string) {
    return req<{ status: string; slots: any[] }>("/workers/availability", { method: "POST", body: JSON.stringify({ dayOfWeek, startTime, endTime }) });
  },
  async deleteAvailabilitySlot(id: string) {
    return req<{ status: string; slots: any[] }>(`/workers/availability/${encodeURIComponent(id)}`, { method: "DELETE" });
  },
  // Increment 30: job applications
  async applyToJob(jobId: string, coverNote?: string) {
    return req<{ application: any }>(`/jobs/${encodeURIComponent(jobId)}/apply`, { method: "POST", body: JSON.stringify({ coverNote }) });
  },
  async withdrawApplication(jobId: string) {
    return req<{ application: any }>(`/jobs/${encodeURIComponent(jobId)}/withdraw`, { method: "POST" });
  },
  async myJobApplication(jobId: string) {
    const r = await req<{ application: any | null }>(`/jobs/${encodeURIComponent(jobId)}/application/me`);
    return r?.application ?? null;
  },
  // QA #22: job ids the user has already applied to (for "Applied" state on lists)
  async myAppliedJobIds() {
    const r = await req<{ jobIds: string[] }>("/jobs/applications/mine");
    return r?.jobIds ?? [];
  },
  async jobApplicants(jobId: string) {
    return req<{ job: any; applications: any[] }>(`/jobs/${encodeURIComponent(jobId)}/applications`);
  },
  async setApplicationStatus(appId: string, status: "shortlisted" | "accepted" | "rejected") {
    return req<{ application: any; jobClosed?: boolean }>(`/applications/${encodeURIComponent(appId)}/status`, { method: "POST", body: JSON.stringify({ status }) });
  },
  async myApplications() {
    const r = await req<{ applications: any[] }>("/applications/me");
    return r?.applications ?? [];
  },
  // Increment 26: worker credentials (worker side)
  async myCredentials() {
    const r = await req<{ credentials: any[] }>("/workers/credentials");
    return r?.credentials ?? [];
  },
  async addCredential(input: { credentialType: string; credentialNumber?: string; issuer?: string; issueDate?: string; expiryDate?: string }) {
    return req<{ credential: any }>("/workers/credentials", { method: "POST", body: JSON.stringify(input) });
  },
  async uploadCredentialDoc(id: string, file: File) {
    const token = getToken();
    const res = await fetch(`${API_BASE}/workers/credentials/${encodeURIComponent(id)}/doc`, {
      method: "POST", credentials: "include",
      headers: { "Content-Type": file.type || "application/octet-stream", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: file,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error((data as any)?.error || `HTTP ${res.status}`);
    return data;
  },
  async deleteCredential(id: string) {
    return req<{ ok: boolean }>(`/workers/credentials/${encodeURIComponent(id)}`, { method: "DELETE" });
  },
  // Increment 26: worker credentials (admin side)
  async adminWorkerCredentials(status = "pending") {
    const r = await req<{ credentials: any[] }>(`/admin/worker-credentials?status=${encodeURIComponent(status)}`);
    return r?.credentials ?? [];
  },
  async adminApproveCredential(id: string) {
    return req<{ ok: boolean }>(`/admin/worker-credentials/${encodeURIComponent(id)}/approve`, { method: "POST" });
  },
  async adminRejectCredential(id: string, note?: string) {
    return req<{ ok: boolean }>(`/admin/worker-credentials/${encodeURIComponent(id)}/reject`, { method: "POST", body: JSON.stringify({ note }) });
  },
  async adminCredentialDocUrl(id: string): Promise<string | null> {
    const token = getToken();
    const res = await fetch(`${API_BASE}/admin/worker-credentials/${encodeURIComponent(id)}/doc`, {
      credentials: "include", headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
    if (!res.ok) return null;
    return URL.createObjectURL(await res.blob());
  },
  async approveVerification(id: string) {
    return req<{ ok: boolean; status: string }>(`/verification/${encodeURIComponent(id)}/approve`, { method: "POST" });
  },
  async rejectVerification(id: string, note?: string) {
    return req<{ ok: boolean; status: string }>(`/verification/${encodeURIComponent(id)}/reject`, { method: "POST", body: JSON.stringify({ note }) });
  },

  // Increment 15: admin console
  async adminStats() {
    return req<any>("/admin/stats");
  },
  async adminUsers(q?: string) {
    const r = await req<{ users: any[] }>(`/admin/users${q ? `?q=${encodeURIComponent(q)}` : ""}`);
    return r?.users ?? [];
  },
  // v3.2.0 admin portal
  async adminDashboard() {
    return req<any>("/admin/dashboard");
  },
  async adminCustomers(opts: { q?: string; status?: string; type?: string; kyc?: string } = {}) {
    const p = new URLSearchParams();
    for (const [k, v] of Object.entries(opts)) if (v) p.set(k, v as string);
    return req<{ stats: any; rows: any[] }>(`/admin/customers?${p.toString()}`);
  },
  async adminClients(q?: string, status?: string) {
    const p = new URLSearchParams(); if (q) p.set("q", q); if (status) p.set("status", status);
    return req<{ stats: any; rows: any[] }>(`/admin/clients?${p.toString()}`);
  },
  async adminWorkers(q?: string, status?: string, kyc?: string) {
    const p = new URLSearchParams(); if (q) p.set("q", q); if (status) p.set("status", status); if (kyc) p.set("kyc", kyc);
    return req<{ stats: any; rows: any[] }>(`/admin/workers?${p.toString()}`);
  },
  async adminSetMemberKyc(id: number, approve: boolean) {
    return req<{ ok: boolean; workerVerified: boolean }>(`/admin/members/${id}/kyc`, { method: "POST", body: JSON.stringify({ approve }) });
  },
  async adminMemberDetail(id: number) {
    return req<any>(`/admin/members/${id}/detail`);
  },
  async adminAdmins() {
    const r = await req<{ admins: any[] }>("/admin/admins");
    return r?.admins ?? [];
  },
  async adminSetAdminRole(id: number, make: boolean) {
    return req<{ ok: boolean }>(`/admin/users/${id}/admin-role`, { method: "POST", body: JSON.stringify({ make }) });
  },
  async adminEmailOutbox() {
    return req<any>("/admin/email-outbox");
  },
  async adminRetryEmails() {
    return req<{ ok: boolean; retried: number; sent: number }>("/admin/jobs/email-retry", { method: "POST" });
  },
  async adminSuspend(id: number) {
    return req<{ ok: boolean; accountStatus: string }>(`/admin/users/${id}/suspend`, { method: "POST" });
  },
  async adminReactivate(id: number) {
    return req<{ ok: boolean; accountStatus: string }>(`/admin/users/${id}/reactivate`, { method: "POST" });
  },
  async adminListings(type: string, q?: string) {
    const r = await req<{ type: string; listings: any[] }>(`/admin/listings?type=${encodeURIComponent(type)}${q ? `&q=${encodeURIComponent(q)}` : ""}`);
    return r?.listings ?? [];
  },
  async adminModerate(type: string, id: string, action: "hide" | "restore") {
    return req<{ ok: boolean; hidden: boolean }>(`/admin/listings/${encodeURIComponent(type)}/${encodeURIComponent(id)}/${action}`, { method: "POST" });
  },
  async adminReviews(q?: string) {
    const r = await req<{ reviews: any[] }>(`/admin/reviews${q ? `?q=${encodeURIComponent(q)}` : ""}`);
    return r?.reviews ?? [];
  },
  async adminDeleteReview(id: string) {
    return req<{ ok: boolean }>(`/admin/reviews/${encodeURIComponent(id)}`, { method: "DELETE" });
  },
  async adminTransactions() {
    return req<{ orders: any[]; contracts: any[] }>("/admin/transactions");
  },

  // Increment 16: disputes
  async openDispute(payload: { dealType: "order" | "contract"; dealId: string; reason: string; message?: string }) {
    return req<{ dispute: any }>("/disputes", { method: "POST", body: JSON.stringify(payload) });
  },
  async respondDispute(id: string, message?: string) {
    return req<{ dispute: any }>(`/disputes/${encodeURIComponent(id)}/respond`, { method: "POST", body: JSON.stringify({ message }) });
  },
  async myDisputes() {
    const r = await req<{ disputes: any[] }>("/disputes/mine");
    return r?.disputes ?? [];
  },
  async adminDisputeQueue() {
    const r = await req<{ disputes: any[] }>("/disputes/admin/queue");
    return r?.disputes ?? [];
  },
  async resolveDispute(id: string, payload: { resolution: "refund" | "release" | "split"; refundAmount?: number; releaseAmount?: number; note?: string }) {
    return req<{ dispute: any }>(`/disputes/${encodeURIComponent(id)}/resolve`, { method: "POST", body: JSON.stringify(payload) });
  },

  // Increment 17: Companies vertical
  async myCompanies() {
    const r = await req<{ companies: any[] }>("/companies/mine");
    return r?.companies ?? [];
  },
  async companyMembers(companyId: string) {
    return req<{ company: any; myRole: string; members: any[] }>(`/companies/${encodeURIComponent(companyId)}/members`);
  },
  async inviteMember(companyId: string, email: string, role: "admin" | "member") {
    return req<{ member: any }>(`/companies/${encodeURIComponent(companyId)}/members/invite`, { method: "POST", body: JSON.stringify({ email, role }) });
  },
  async changeMemberRole(companyId: string, memberId: string, role: "admin" | "member") {
    return req<{ member: any }>(`/companies/${encodeURIComponent(companyId)}/members/${encodeURIComponent(memberId)}/role`, { method: "POST", body: JSON.stringify({ role }) });
  },
  async removeMember(companyId: string, memberId: string) {
    return req<{ ok: boolean }>(`/companies/${encodeURIComponent(companyId)}/members/${encodeURIComponent(memberId)}`, { method: "DELETE" });
  },
  async myCompanyInvites() {
    const r = await req<{ invites: any[] }>("/companies/invites/mine");
    return r?.invites ?? [];
  },
  async respondInvite(memberId: string, action: "accept" | "decline") {
    return req<{ ok: boolean; status: string }>(`/companies/invites/${encodeURIComponent(memberId)}/${action}`, { method: "POST" });
  },

  // Increment 18: settings + saved items
  async changePassword(currentPassword: string, newPassword: string) {
    return req<{ ok: boolean }>("/auth/password", { method: "POST", body: JSON.stringify({ currentPassword, newPassword }) });
  },
  async savedKeys() {
    const r = await req<{ keys: string[] }>("/saved/keys");
    return r?.keys ?? [];
  },
  async savedList() {
    const r = await req<{ saved: any[] }>("/saved");
    return r?.saved ?? [];
  },
  async saveListing(kind: string, refId: string) {
    return req<{ ok: boolean; saved: boolean }>("/saved", { method: "POST", body: JSON.stringify({ kind, refId }) });
  },
  async unsaveListing(kind: string, refId: string) {
    return req<{ ok: boolean; saved: boolean }>(`/saved/${encodeURIComponent(kind)}/${encodeURIComponent(refId)}`, { method: "DELETE" });
  },

  // Increment 22: vendor credit
  async getCredit() {
    return req<{ limit: number; outstanding: number; available: number; transactions: any[] }>("/credit");
  },
  async repayCredit(amount: number) {
    return req<{ ok: boolean; repaid: number; limit: number; outstanding: number; available: number }>("/credit/repay", { method: "POST", body: JSON.stringify({ amount }) });
  },
  async adminCredit(q?: string) {
    const r = await req<{ accounts: any[] }>(`/admin/credit${q ? `?q=${encodeURIComponent(q)}` : ""}`);
    return r?.accounts ?? [];
  },
  async adminSetCreditLimit(userId: number, limit: number) {
    return req<{ ok: boolean; limit: number; outstanding: number; available: number }>(`/admin/credit/${userId}/limit`, { method: "POST", body: JSON.stringify({ limit }) });
  },

  // Increment 19: unified search
  async search(q: string, type?: string) {
    const qs = new URLSearchParams({ q });
    if (type) qs.set("type", type);
    return req<{ query: string; results: any[]; counts: Record<string, number>; total: number }>(`/search?${qs.toString()}`);
  },
};

export const ACTIVITY_LABELS: Record<string, string> = {
  find_work: "Find Work", offer_service: "Offer a Service", promote_company: "Promote a Company",
  hire: "Hire Someone", post_job: "Post a Job", post_project: "Post a Project",
};
